import { faGears, faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack } from '@sixui/ui';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { QuerySkeleton } from '~/components/atoms/QuerySkeleton';
import { UserAvatar } from '~/components/atoms/UserAvatar';
import { UpdateAppUserForm } from '~/components/forms/AppUserForm';
import { Breadcrumbs } from '~/components/molecules/Breadcrumbs';
import { Heading } from '~/components/molecules/Heading';
import { AppBreadcrumbs } from '~/components/organisms/Breadcrumbs';
import { useNavigate } from '~/components/utils/Navigation';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { useAppUserQuery } from '~/lib/api/modules/Apps/hooks/useAppUserQuery';

export type IAppUpdateUserPageProps = {
  appId: string;
  userId: string;
};

export const AppUpdateUserPage: React.FC<IAppUpdateUserPageProps> = (props) => {
  const { appId, userId } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const appUserQuery = useAppUserQuery({
    appId,
    userId,
  });
  const userInfo = appUserQuery.data
    ? parseUserInfo(appUserQuery.data)
    : undefined;
  const parentUrl = `/apps/${appId}/settings/users`;

  return (
    <>
      <Helmet>
        <title>{t('AppUpdateUserPage.seo.title')}</title>
      </Helmet>

      <AppBreadcrumbs appId={appId}>
        <Breadcrumbs.Action href={`/apps/${appId}/settings`}>
          <FontAwesomeIcon icon={faGears} />
          {t('common.settings')}
        </Breadcrumbs.Action>
        <Breadcrumbs.Action href={parentUrl}>
          <FontAwesomeIcon icon={faUserGroup} />
          {t('common.appUsers')}
        </Breadcrumbs.Action>
      </AppBreadcrumbs>

      <Heading
        title={
          <Stack horizontal gap={2}>
            <QuerySkeleton query={appUserQuery} variant="circular">
              <UserAvatar user={appUserQuery.data} />
            </QuerySkeleton>
            <QuerySkeleton query={appUserQuery} length={{ min: 8, max: 16 }}>
              {userInfo?.fullName}
            </QuerySkeleton>
          </Stack>
        }
        subtitle={t('AppUpdateUserPage.subtitle')}
        previousUrl={parentUrl}
      />

      <UpdateAppUserForm
        appId={appId}
        userId={userId}
        onSuccess={() => navigate(parentUrl)}
        cancelUrl={parentUrl}
      />
    </>
  );
};
